import useBreakpoint from 'hooks/useBreakpoint';
import { bkptVal } from 'styles/globals';
import { type MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RawHtml from 'utils/miscUtils/rawHtml';
import { withErrorBoundary } from 'components/ErrorBoundary';
import { UIFragments } from 'utils/log/constants/uiFragments';
import * as S from './TableOfContentsStyles';

export interface TableOfContentsProps {
  contents: string;
  title: string;
  headerHeight: number;
  className?: string;
}

const TableOfContents = ({ title, contents, headerHeight, className }: TableOfContentsProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isMobileOrTablet = useBreakpoint('lgMax');
  const sectionIds = useMemo(
    () => (contents.match(/href="#([^"]*)"/g) || []).map((href) => href.slice(7, -1)),
    [contents],
  );
  const activeSectionRef = useRef<Element | null>(null);

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLAnchorElement;
    if (
      target &&
      target.tagName === 'A' &&
      (target.getAttribute('href') || '').startsWith('#') &&
      !(target.classList.contains('closed') || target.classList.contains('open'))
    ) {
      setDropdownOpen(false);
    }
  };

  const handleSubmenu = useCallback((event: MouseEvent) => {
    const target = event.target as Element;
    if (target.classList.contains('open')) {
      target.nextElementSibling?.querySelectorAll('a.open')?.forEach((element) => {
        element.classList.remove('open');
        element.classList.add('closed');
      });
    }
    target.classList.toggle('open');
    target.classList.toggle('closed');
  }, []);

  useEffect(() => {
    const allListElements: HTMLElement[] = Array.from(document.querySelectorAll('#toc-wrapper a:not(:only-child)'));
    allListElements.forEach((element) => {
      if (!element.classList.contains('open')) {
        element.classList.add('closed');
      }
      element.addEventListener('click', handleSubmenu);
    });

    return () => {
      allListElements.map((element) => element.removeEventListener('click', handleSubmenu));
    };
  }, [handleSubmenu]);

  useEffect(() => {
    const setActiveSection = () => {
      const firstBelowTopIdx = sectionIds.findIndex((id) => {
        if (!id) return false;
        const element = document.querySelector(`#${id}`);
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return element && rect?.top >= headerHeight + (document.querySelector('.toc-mobile')?.clientHeight ?? 0) + 25;
      });
      const nextActiveSection = sectionIds[firstBelowTopIdx === -1 ? sectionIds.length - 1 : firstBelowTopIdx - 1];
      const menuClass = window.innerWidth > bkptVal.lg * 16 ? '.toc-desktop' : '.toc-mobile';
      const nextActiveAnchorElement = document.querySelector(`${menuClass} a[href="#${nextActiveSection}"]`);
      if (nextActiveAnchorElement !== activeSectionRef?.current) {
        document.querySelectorAll('a.active-link').forEach((element) => {
          element.classList?.remove('active-link');
        });
        const elementIsInClosedMenu =
          nextActiveAnchorElement?.parentElement?.parentElement?.parentElement?.querySelector(':scope > a.closed');
        if (!elementIsInClosedMenu) {
          nextActiveAnchorElement?.classList?.add('active-link');
          activeSectionRef.current = nextActiveAnchorElement || null;
        } else {
          const closestVisibleAnchorParent = nextActiveAnchorElement
            ?.closest(`${menuClass} a.opened + ul > li, ${menuClass} > .content-wrapper > ul > li`)
            ?.querySelector('a.closed');
          closestVisibleAnchorParent?.classList?.add('active-link');
          activeSectionRef.current = closestVisibleAnchorParent || null;
        }
      }
    };

    document.addEventListener('scroll', setActiveSection);
    document.addEventListener('resize', setActiveSection);

    return () => {
      document.removeEventListener('scroll', setActiveSection);
      document.removeEventListener('resize', setActiveSection);
    };
  }, [sectionIds, headerHeight]);

  return (
    <S.NavigationWrapper
      id='toc-wrapper'
      data-cy='TOC'
      className={className}
      $headerHeight={headerHeight - 1}
    >
      <S.MobileWrapper
        className='toc-mobile'
        $isOpened={dropdownOpen && isMobileOrTablet}
      >
        <S.TableTitle className='dropdown'>{title}</S.TableTitle>
        <S.DescriptionAndArrowWrapper
          onClick={() => setDropdownOpen(!dropdownOpen)}
          $dropdownOpened={dropdownOpen && isMobileOrTablet}
        >
          <span>Jump to your section</span>
        </S.DescriptionAndArrowWrapper>
        <S.ContentWrapper
          className='content-wrapper'
          key='contents'
          onClick={handleClick}
          $headerHeight={headerHeight}
          $isOpened={dropdownOpen && isMobileOrTablet}
          $isDropdown
        >
          {RawHtml({ html: contents })}
        </S.ContentWrapper>
      </S.MobileWrapper>
      <S.DesktopWrapper
        className='toc-desktop'
        $headerHeight={headerHeight - 1}
      >
        <S.TableTitle>{title}</S.TableTitle>
        <S.ContentWrapper
          className='content-wrapper'
          key='contents'
          $headerHeight={headerHeight}
        >
          {RawHtml({ html: contents })}
        </S.ContentWrapper>
      </S.DesktopWrapper>
    </S.NavigationWrapper>
  );
};

export default withErrorBoundary(TableOfContents, { fragment: UIFragments.TOC });
